import React from "react";
import classNames from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import { withTranslation } from "react-i18next";
import { Element, scroller } from "react-scroll";
import ReactGA from "react-ga";
import history from "../../../Utils/history";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Favorite from "@material-ui/icons/Favorite";
import ButtonBase from "@material-ui/core/ButtonBase";
import Hidden from "@material-ui/core/Hidden";
import Typography from "@material-ui/core/Typography";
import FacebookIcon from "@material-ui/icons/Facebook";
import InstagramIcon from "@material-ui/icons/Instagram";
import YouTubeIcon from "@material-ui/icons/YouTube";
import TwitterIcon from "@material-ui/icons/Twitter";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
// core components
import Header from "../../Layout/GuestLayout/Header";
import Footer from "../../Layout/GuestLayout/Footer";
import GridContainer from "../../Layout/GuestLayout/GridContainer";
import GridItem from "../../Layout/GuestLayout/GridItem";
import HeaderLinks from "../../Layout/GuestLayout/HeaderLinks";
import Parallax from "../../Elements/Parallax";
import Button from "../../Elements/CustomButtonMuiKit";

import landingPageStyle from "../../../Utils/MaterialKitThemeJss/landingPageStyle";

import LoginForm from "../../Forms/LoginForm";
import IosDownload from "../../../Images/appleapp-350.png";
import AndroidDownload from "../../../Images/google-play-350.png";
import ServicesSection from "../../Layout/GuestLayout/ServicesSection";
import FeaturesSection from "../../Layout/GuestLayout/FeaturesSection";
import GuestRates from "../../Layout/GuestLayout/GuestRates";
import TestimonialSection from "../../Layout/GuestLayout/TestimonialSection";
import ContactUsSection from "../../Layout/GuestLayout/ContactUsSection";
import LandingPageAnnouncements from "../../Cards/Announcements/LandingPageAnnouncements";

// Become a Reseller CTA palette. Warm gold/amber pops against the cool, dark hero
// photo; the deep navy text keeps it readable (white on amber fails contrast).
const CTA_GRADIENT =
  "linear-gradient(120deg, #ffd45e 0%, #ffa41b 45%, #ff7a45 100%)";
const CTA_TEXT = "#11263d";
const CTA_GLOW = "rgba(255, 149, 27, 0.4)";
const CTA_GLOW_STRONG = "rgba(255, 149, 27, 0.65)";

// Cool teal/blue alternative, closer to the navy + teal brand palette.
// Swap it in by commenting out the four constants above and uncommenting these.
// const CTA_GRADIENT =
//   "linear-gradient(120deg, #13c2b4 0%, #0a7ea4 50%, #1d4ed8 100%)";
// const CTA_TEXT = "#ffffff";
// const CTA_GLOW = "rgba(13, 148, 180, 0.45)";
// const CTA_GLOW_STRONG = "rgba(29, 78, 216, 0.7)";

const styles = (theme) => ({
  dowloadAppImg: {
    width: "100%",
    maxWidth: 200,
    height: "auto",
    [theme.breakpoints.only("xs")]: {
      maxWidth: 135,
    },
  },
  appButtonsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  // Badges and CTA share one wrapper so the button spans exactly the two badges
  ctaBlock: {
    maxWidth: 416,
    [theme.breakpoints.down("sm")]: {
      marginLeft: "auto",
      marginRight: "auto",
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.only("xs")]: {
      maxWidth: 286,
    },
  },
  "@keyframes resellerGlow": {
    "0%, 100%": { boxShadow: `0 8px 22px ${CTA_GLOW}` },
    "50%": { boxShadow: `0 12px 32px ${CTA_GLOW_STRONG}` },
  },
  // "&&" doubles specificity so these win over the MUI-Kit button classes
  resellerButton: {
    "&&": {
      position: "relative",
      overflow: "hidden",
      marginTop: theme.spacing(1),
      marginLeft: 0,
      marginRight: 0,
      padding: "15px 30px",
      fontSize: 15,
      fontWeight: 800,
      letterSpacing: 1,
      color: CTA_TEXT,
      backgroundColor: "transparent",
      backgroundImage: CTA_GRADIENT,
      // Wider than the button so the gradient can slide on hover
      backgroundSize: "200% auto",
      backgroundPosition: "left center",
      transition:
        "background-position 0.6s ease, box-shadow 0.3s ease, transform 0.2s ease",
      animation: "$resellerGlow 3.2s ease-in-out infinite",
    },
    "&&:hover,&&:focus": {
      color: CTA_TEXT,
      backgroundImage: CTA_GRADIENT,
      backgroundPosition: "right center",
      boxShadow: `0 14px 34px ${CTA_GLOW_STRONG}`,
      transform: "translateY(-2px)",
      animation: "none",
    },
    "&&:active": {
      transform: "translateY(0)",
    },
    // Light sweeping across the button on hover
    "&&:after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-60%",
      width: "40%",
      height: "100%",
      background:
        "linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 100%)",
      transform: "skewX(-20deg)",
      transition: "left 0.7s ease",
    },
    "&&:hover:after": {
      left: "120%",
    },
    "&& svg": {
      marginLeft: 8,
      fontSize: 18,
      verticalAlign: "text-bottom",
      transition: "transform 0.2s ease",
    },
    "&&:hover svg": {
      transform: "translateX(4px)",
    },
    "@media (prefers-reduced-motion: reduce)": {
      "&&": { animation: "none", transition: "none" },
      "&&:after": { display: "none" },
    },
  },
  slides: {
    [theme.breakpoints.up("lg")]: {
      marginLeft: "15%",
      marginRight: "15%",
    },
  },
  // Spans the two app store badges above it
  resellerButtonContainer: {
    maxWidth: 416,
    marginTop: theme.spacing(1),
    [theme.breakpoints.only("xs")]: {
      maxWidth: 286,
    },
  },
});

// English regardless of locale - marketing filters leads on this tag
const RESELLER_LEAD_SOURCE = "Become a Reseller";

const useStyles = makeStyles(landingPageStyle);
const useLocalStyles = makeStyles(styles);

// const goToDownloadAppPage = () => {
//   window.open(`http://bit.ly/2pZiIvN`, "_blank");
// };

function LandingPage({ t, ...rest }) {
  const [leadSource, setLeadSource] = React.useState("");
  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    ReactGA.pageview(window.location.pathname + window.location.search);
  });
  const goToSocialSite = (url) => {
    window.open(`${url}`, "_blank");
  };
  const classes = useStyles();
  const localClasses = useLocalStyles();

  const openIosStore = () => {
    const url =
      "https://itunes.apple.com/us/app/sin-pin-recharge/id1439216848?ls=1&mt=8";

    window.open(url, "_blank");
  };

  const openAndroidStore = () => {
    const url =
      "https://play.google.com/store/apps/details?id=com.sinpin.customerapp";

    window.open(url, "_blank");
  };

  const goToResellerForm = () => {
    setLeadSource(RESELLER_LEAD_SOURCE);
    ReactGA.event({
      category: "Landing Page",
      action: "Click",
      label: RESELLER_LEAD_SOURCE,
    });
    scroller.scrollTo("contacts", {
      duration: 800,
      delay: 0,
      smooth: "easeInOutQuart",
      offset: -90,
    });
  };

  const appStoreAndResellerBlock = (
    <div className={localClasses.ctaBlock}>
      <div className={localClasses.appButtonsContainer}>
        <ButtonBase onClick={openIosStore}>
          <img
            src={IosDownload}
            alt="apple"
            className={localClasses.dowloadAppImg}
          />
        </ButtonBase>
        <ButtonBase onClick={openAndroidStore}>
          <img
            src={AndroidDownload}
            alt="google"
            className={localClasses.dowloadAppImg}
          />
        </ButtonBase>
      </div>
      <Button
        className={localClasses.resellerButton}
        round
        fullWidth
        onClick={goToResellerForm}
      >
        {t("landingPage.becomeAReseller")}
        <ArrowForwardIcon />
      </Button>
    </div>
  );

  return (
    <div>
      <Header
        color="transparent"
        links={<HeaderLinks dropdownHoverColor="info" />}
        fixed
        changeColorOnScroll={{
          height: 65,
          color: "info",
        }}
        {...rest}
      />
      <Parallax
        image={require("../../../Images/girl-talking-on-phone.jpg")}
        filter="dark"
      >
        <div className={classes.container}>
          <GridContainer spacing={6}>
            <Hidden smDown>
              <GridItem xs={12} md={5} lg={6}>
                <h1 className={classes.title}>
                  {t("landingPage.stayConnected")}
                </h1>
                <h4 style={{ fontWeight: 400 }}>
                  {t("landingPage.stayConnectedDetails")}
                </h4>
                <br />
                {appStoreAndResellerBlock}
              </GridItem>
            </Hidden>
            <GridItem xs={12} md={7} lg={6}>
              <div className={classes.loginFormContainer}>
                <LoginForm />
                <Hidden mdUp>{appStoreAndResellerBlock}</Hidden>
              </div>
            </GridItem>
          </GridContainer>
        </div>
      </Parallax>
      <div className={classNames(classes.main, classes.mainRaised)}>
        <div className={classes.containerFluid}>
          <div className={localClasses.slides}>
            <LandingPageAnnouncements />
          </div>
          <Element name="services" className="element" id="services">
            <ServicesSection />
          </Element>
          <Element name="features" className="element" id="features">
            <FeaturesSection />
          </Element>
          <Element name="guestrates" className="element" id="guestrates">
            <GuestRates />
          </Element>
          <Element name="testimonials" className="element" id="testimonials">
            <TestimonialSection />
          </Element>
          <Element name="contacts" className="element" id="contacts">
            <ContactUsSection
              source={leadSource}
              prefillMessage={t("landingPage.resellerInterestMessage")}
            />
          </Element>
        </div>
      </div>
      <Footer
        className={classes.footer}
        content={
          <div style={{ marginTop: 5, marginBottom: 5 }}>
            <div
              className={classes.left}
              style={{ marginTop: 5, marginBottom: 5 }}
            >
              <List className={classes.list}>
                <ListItem className={classes.inlineBlock}>
                  <Typography
                    variant="h6"
                    component="h2"
                    color="primary"
                    style={{ fontSize: 13, marginTop: 5 }}
                  >
                    {`${t("landingPage.followUs")}:`}
                  </Typography>
                </ListItem>
                <ListItem className={classes.inlineBlock}>
                  <Button
                    color="facebook"
                    justIcon
                    size="sm"
                    round
                    style={{ marginLeft: 8 }}
                    onClick={() =>
                      goToSocialSite("https://www.facebook.com/mysinpin/")
                    }
                  >
                    <FacebookIcon />
                  </Button>
                </ListItem>
                <ListItem className={classes.inlineBlock}>
                  <Button
                    color="youtube"
                    justIcon
                    size="sm"
                    round
                    style={{ marginLeft: 8 }}
                    onClick={() =>
                      goToSocialSite(
                        "https://www.youtube.com/channel/UC4h04-eFsJ0dN6ELBGfMeRA"
                      )
                    }
                  >
                    <YouTubeIcon />
                  </Button>
                </ListItem>
                <ListItem className={classes.inlineBlock}>
                  <Button
                    color="instagram"
                    justIcon
                    size="sm"
                    round
                    style={{ marginLeft: 8 }}
                    onClick={() =>
                      goToSocialSite("https://www.instagram.com/sinpin_1click/")
                    }
                  >
                    <InstagramIcon />
                  </Button>
                </ListItem>
                <ListItem className={classes.inlineBlock}>
                  <Button
                    color="twitter"
                    justIcon
                    round
                    size="sm"
                    style={{ marginLeft: 8 }}
                    onClick={() =>
                      goToSocialSite("https://twitter.com/mysinpin")
                    }
                  >
                    <TwitterIcon />
                  </Button>
                </ListItem>
                <ListItem className={classes.inlineBlock}>
                  <ButtonBase
                    className={classes.block}
                    onClick={() => history.push("/home/terms")}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      color="primary"
                      style={{ fontSize: 11 }}
                    >
                      {t("landingPage.termsOfService")}
                    </Typography>
                  </ButtonBase>
                </ListItem>
                <ListItem className={classes.inlineBlock}>
                  <ButtonBase
                    className={classes.block}
                    onClick={() => history.push("/home/privacy")}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      color="primary"
                      style={{ fontSize: 11 }}
                    >
                      {t("landingPage.privacyPolicy")}
                    </Typography>
                  </ButtonBase>
                </ListItem>
              </List>
            </div>
            <div className={classes.right}>
              <Typography
                variant="h6"
                component="h2"
                color="primary"
                style={{ fontSize: 13 }}
              >
                &copy; {1900 + new Date().getYear()} Look International Inc -{" "}
                {t("landingPage.madeWith")}{" "}
                <Favorite className={classes.icon} color="secondary" />{" "}
                {t("landingPage.byTheTeam")}{" "}
                <a
                  href="tel:8778055665"
                  style={{ color: "#f4645a", fontWeight: "bold" }}
                  // style={{ color: "#00457b" }}
                >
                  (877) 805-5665
                </a>
              </Typography>
            </div>
          </div>
        }
      />
    </div>
  );
}

export default withTranslation()(LandingPage);
